/**
 * Bếp của bà recipes (minigames.md §4.5): each level is one dish cooked in 5–7 small steps, grouped into the
 * four lines of the recipe card. Steps only name what happens; the actions (actions.ts) draw and judge it.
 */

export type Station = 'board' | 'stove' | 'table';
export type RecipeId = 'jam' | 'noodle' | 'kimbap';
export type ActionKind = 'wash' | 'tap' | 'drag' | 'stir' | 'hold' | 'slice' | 'spread' | 'roll' | 'plate';

/** Prop sprites (`mg_cook_<name>` in the minigames atlas, vector fallback otherwise). */
export type Prop =
  | 'strawberry'
  | 'strawberry_bare'
  | 'sugar'
  | 'scallion'
  | 'scallion_cut'
  | 'noodle'
  | 'egg'
  | 'egg_open'
  | 'sausage'
  | 'mint'
  | 'cucumber'
  | 'cucumber_cut'
  | 'lettuce'
  | 'kimbap_slice'
  | 'label'
  | 'carrot_flower';

export type CookStep =
  | { action: 'wash'; station: 'board'; item: 'strawberry' | 'lettuce'; count: number }
  | { action: 'tap'; station: 'board' | 'stove'; item: 'stem' | 'egg'; count: number; taps: number }
  | {
      action: 'drag';
      station: 'board' | 'stove';
      target: 'pot' | 'rice';
      items: readonly Prop[];
      /** Sếp Mèo's pâté: never needed; dropping it in makes the cat eat it (§4.5). */
      decoy?: boolean;
    }
  | { action: 'stir'; station: 'stove'; turns: number }
  | { action: 'hold'; station: 'stove' | 'table'; kind: 'pour' | 'boil' }
  | { action: 'slice'; station: 'board'; item: 'scallion' | 'cucumber' | 'kimbap'; cuts: number }
  | { action: 'spread'; station: 'board' }
  | { action: 'roll'; station: 'board' }
  | { action: 'plate'; station: 'table'; dish: 'jars' | 'bowl' | 'bento'; garnishes: readonly Prop[] };

export type RecipeStep = CookStep & { /** 1-based line on the recipe card. */ line: 1 | 2 | 3 | 4 };

export interface Recipe {
  id: RecipeId;
  level: 1 | 2 | 3;
  steps: readonly RecipeStep[];
}

export const RECIPES: Record<RecipeId, Recipe> = {
  jam: {
    id: 'jam',
    level: 1,
    steps: [
      { line: 1, action: 'wash', station: 'board', item: 'strawberry', count: 6 },
      { line: 2, action: 'tap', station: 'board', item: 'stem', count: 6, taps: 1 },
      { line: 3, action: 'drag', station: 'stove', target: 'pot', items: ['strawberry_bare', 'sugar'] },
      { line: 3, action: 'stir', station: 'stove', turns: 3 },
      { line: 4, action: 'hold', station: 'table', kind: 'pour' },
      { line: 4, action: 'plate', station: 'table', dish: 'jars', garnishes: ['label', 'label', 'label'] },
    ],
  },
  noodle: {
    id: 'noodle',
    level: 2,
    steps: [
      { line: 1, action: 'slice', station: 'board', item: 'scallion', cuts: 3 },
      { line: 2, action: 'hold', station: 'stove', kind: 'boil' },
      { line: 3, action: 'drag', station: 'stove', target: 'pot', items: ['noodle'] },
      { line: 3, action: 'tap', station: 'stove', item: 'egg', count: 1, taps: 2 },
      {
        line: 4,
        action: 'plate',
        station: 'table',
        dish: 'bowl',
        garnishes: ['scallion_cut', 'egg_open', 'sausage', 'mint'],
      },
    ],
  },
  kimbap: {
    id: 'kimbap',
    level: 3,
    steps: [
      { line: 1, action: 'wash', station: 'board', item: 'lettuce', count: 2 },
      { line: 1, action: 'slice', station: 'board', item: 'cucumber', cuts: 2 },
      { line: 2, action: 'spread', station: 'board' },
      {
        line: 3,
        action: 'drag',
        station: 'board',
        target: 'rice',
        items: ['lettuce', 'cucumber_cut', 'egg_open', 'sausage'],
        decoy: true,
      },
      { line: 3, action: 'roll', station: 'board' },
      { line: 4, action: 'slice', station: 'board', item: 'kimbap', cuts: 4 },
      {
        line: 4,
        action: 'plate',
        station: 'table',
        dish: 'bento',
        garnishes: ['carrot_flower', 'mint', 'lettuce'],
      },
    ],
  },
};

/** The step's one-line instruction (`ui.cooking.step.*`). */
export function stepHintKey(step: CookStep): string {
  switch (step.action) {
    case 'tap':
      return step.item === 'egg' ? 'ui.cooking.step.tap_egg' : 'ui.cooking.step.tap_stem';
    case 'drag':
      return step.target === 'rice' ? 'ui.cooking.step.drag_fill' : 'ui.cooking.step.drag';
    case 'hold':
      return step.kind === 'boil' ? 'ui.cooking.step.hold_boil' : 'ui.cooking.step.hold_pour';
    case 'plate':
      return step.dish === 'jars' ? 'ui.cooking.step.plate_labels' : 'ui.cooking.step.plate';
    case 'wash':
      return 'ui.cooking.step.wash';
    case 'stir':
      return 'ui.cooking.step.stir';
    case 'slice':
      return 'ui.cooking.step.slice';
    case 'spread':
      return 'ui.cooking.step.spread';
    case 'roll':
      return 'ui.cooking.step.roll';
  }
}

/** Steps whose quality makes up the score (§4.6). */
export const isScored = (step: CookStep): boolean => step.action === 'hold' || step.action === 'slice';
